// src/context/WebSocketContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { WS_URL } from "../api/websocket";
import { fetchWrapper } from "../utils/fetchWrapper";

const WebSocketContext = createContext(null);

export const useWebSocketData = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const [topics, setTopics] = useState({
    leads: [],
    visits: [],
    suspicious: [],
    deployments: [],
    live: [],
    "pm2-logs": [],
    "nginx-access-logs": [],
    "nginx-error-logs": [],
    sslcertification: [],
    "script-logs": [],
    analytics: null, // Object for live analytics data
  });

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const [instantDataLoaded, setInstantDataLoaded] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch instant health data on mount
  useEffect(() => {
    const fetchInstantData = async () => {
      setLoading(true);
      try {
        const response = await fetchWrapper.get('/health/instant');

        if (response.data.success && response.data.data) {
          // Populate live topic with instant data
          setTopics(prev => ({
            ...prev,
            live: response.data.data
          }));
        }
      } catch (err) {
        console.error('Failed to fetch instant health data:', err);
      } finally {
        setInstantDataLoaded(true);
        setLoading(false);
      }
    };

    fetchInstantData();
  }, []);

  useEffect(() => {
    function connect() {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

      wsRef.current = new WebSocket(WS_URL);

      wsRef.current.onopen = () => console.log("✅ Connected to WebSocket");

      wsRef.current.onmessage = (msg) => {
        try {
          const { topic, data } = JSON.parse(msg.data);
          
          setTopics((prev) => {
            if (!prev.hasOwnProperty(topic)) return prev; // skip unknown topic

            // Analytics is a single object, not an array
            if (topic === 'analytics') {
              return { ...prev, analytics: data };
            }

            // For 'live' topic, merge with existing instant data
            // Update existing entry or add new one
            if (topic === 'live') {
              const existingIndex = prev[topic].findIndex(
                item => item.domain === data.domain && item.event === data.event
              );

              if (existingIndex >= 0) {
                // Update existing entry
                const updated = [...prev[topic]];
                updated[existingIndex] = data;
                return { ...prev, [topic]: updated };
              } else {
                // Add new entry
                return {
                  ...prev,
                  [topic]: [data, ...prev[topic]].slice(0, 100), // Keep last 100
                };
              }
            }

            // Other topics are arrays
            return {
              ...prev,
              [topic]: [data, ...prev[topic]].slice(0, 50),
            };
          });
        } catch (err) {
          console.error("WS parse error:", err);
        }
      };

      wsRef.current.onclose = () => {
        console.warn("⚠️ WS disconnected, reconnecting in 5s...");
        reconnectTimer.current = setTimeout(connect, 5000);
      };

      wsRef.current.onerror = (err) => {
        console.error("WS error:", err);
        wsRef.current.close();
      };
    }

    // Only connect to WebSocket after instant data is loaded (or attempted)
    if (instantDataLoaded) {
      connect();
    }

    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [instantDataLoaded]);

  return (
    <WebSocketContext.Provider value={{ ...topics, loading }}>
      {children}
    </WebSocketContext.Provider>
  );
};
